import { useState, useEffect } from "react";
import { Link } from "react-router";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { systemAdminService } from "@/services/systemAdminService";
import { handleApiError } from "@/services/api";
import type { FarmListItem } from "@/types/models";

export default function FarmsPage() {
  const [farms, setFarms] = useState<FarmListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create farm modal
  const [createOpen, setCreateOpen] = useState(false);
  const [farmName, setFarmName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [saving, setSaving] = useState(false);

  // PIN result modal
  const [pinResult, setPinResult] = useState<{
    pin: string;
    userName: string;
    phone: string;
    farmName: string;
    email: string;
    context: string;
  } | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const loadFarms = async () => {
    setError("");
    try {
      const data = await systemAdminService.listFarms();
      setFarms(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFarms();
  }, []);

  const handleCreateFarm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const result = await systemAdminService.createFarm({
        farm_name: farmName,
        owner_name: ownerName,
        owner_phone: ownerPhone,
        owner_email: ownerEmail || undefined,
      });
      const createdEmail = ownerEmail;
      const createdPhone = ownerPhone;
      const createdFarmName = result.farm.name;
      setCreateOpen(false);
      setFarmName("");
      setOwnerName("");
      setOwnerPhone("");
      setOwnerEmail("");
      setEmailSent(false);
      setPinResult({
        pin: result.pin,
        userName: result.user.name,
        phone: createdPhone,
        farmName: createdFarmName,
        email: createdEmail,
        context: `Farm "${createdFarmName}" created with owner "${result.user.name}"`,
      });
      loadFarms();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      <PageMeta title="Farms | Poultry Book" description="Manage farms" />
      <PageBreadcrumb pageTitle="Farms" />

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">
          Farms
        </h2>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          Create Farm
        </Button>
      </div>

      <div className="card">
        {error && !createOpen && (
          <div className="mx-5 my-4 rounded-lg bg-error-50 px-4 py-3 dark:bg-error-500/10">
            <p className="text-sm text-error-600 dark:text-error-400">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : farms.length === 0 && !error ? (
          <div className="px-5 py-12 text-center text-gray-500 dark:text-gray-400">
            No farms yet. Create your first farm to get started.
          </div>
        ) : farms.length === 0 ? null : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Farm Name</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Owner</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Users</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Created</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">Actions</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {farms.map((farm) => (
                  <TableRow key={farm.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      {farm.name}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {farm.owner_name || "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {farm.user_count}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(farm.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-right">
                      <Link
                        to={`/system/farms/${farm.id}`}
                        className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                      >
                        View Users
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create Farm Modal */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} className="max-w-md p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          Create New Farm
        </h4>

        {error && createOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleCreateFarm} className="space-y-4">
          <div>
            <Label htmlFor="farmName">Farm Name *</Label>
            <Input
              id="farmName"
              value={farmName}
              onChange={(e) => setFarmName(e.target.value)}
              placeholder="e.g., Sri Lakshmi Poultry Farm"
              required
            />
          </div>
          <div>
            <Label htmlFor="ownerName">Owner Name *</Label>
            <Input
              id="ownerName"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Farm owner's name"
              required
            />
          </div>
          <div>
            <Label htmlFor="ownerPhone">Owner Phone *</Label>
            <Input
              id="ownerPhone"
              type="tel"
              value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value.replace(/\D/g, "").slice(0, 15))}
              placeholder="10-digit phone number"
              required
            />
          </div>
          <div>
            <Label htmlFor="ownerEmail">Owner Email (for OTP verification)</Label>
            <Input
              id="ownerEmail"
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="e.g. owner@example.com"
              maxLength={255}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !farmName || !ownerName || !ownerPhone}>
              {saving ? "Creating..." : "Create Farm"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* PIN Result Modal */}
      <Modal isOpen={!!pinResult} onClose={() => setPinResult(null)} showCloseButton={false} className="max-w-sm p-5 sm:p-8 mx-4">
        {pinResult && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-50 dark:bg-success-500/10">
              <svg className="h-6 w-6 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">{pinResult.context}</p>
            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              PIN for {pinResult.userName}:
            </p>
            <div className="my-4 flex items-center justify-center gap-3">
              <span className="rounded-lg bg-gray-100 px-6 py-3 font-mono text-2xl font-bold tracking-[0.3em] text-gray-900 dark:bg-gray-800 dark:text-white">
                {pinResult.pin}
              </span>
              <button
                onClick={() => copyToClipboard(pinResult.pin)}
                className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
                title="Copy PIN"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            <p className="mb-4 text-xs text-warning-500 dark:text-warning-400">
              This PIN will not be shown again. Please note it down.
            </p>
            <div className="flex flex-col items-center gap-3">
              {pinResult.email && !emailSent && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  disabled={sendingEmail}
                  onClick={async () => {
                    setSendingEmail(true);
                    try {
                      await systemAdminService.sendPINEmail({
                        email: pinResult.email,
                        user_name: pinResult.userName,
                        phone: pinResult.phone,
                        farm_name: pinResult.farmName,
                        pin: pinResult.pin,
                      });
                      setEmailSent(true);
                    } catch (err) {
                      setError(handleApiError(err));
                    } finally {
                      setSendingEmail(false);
                    }
                  }}
                >
                  {sendingEmail ? "Sending..." : "Send PIN via Email"}
                </Button>
              )}
              {emailSent && (
                <span className="text-sm text-success-500">Sent to {pinResult.email}</span>
              )}
              <Button size="sm" onClick={() => setPinResult(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
